import { Link } from "react-router-dom";
import RichText from "@/components/RichText";
import VideoEmbed from "./VideoEmbed";
import { Phone, Quote, Mail, MapPin } from "lucide-react";

const Section = ({ children, className = "" }) => (
  <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 md:py-16 ${className}`}>{children}</section>
);

function CtaLink({ url, label, testid, variant = "primary" }) {
  const cls = variant === "primary"
    ? "bg-emerald-800 text-amber-50 hover:bg-emerald-900"
    : "bg-[#C85A32] text-white hover:bg-[#b24d28]";
  const common = `inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors shadow-sm ${cls}`;
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("tel:") || url.startsWith("mailto:"))
    return <a href={url} className={common} data-testid={testid}>{label}</a>;
  return <Link to={url} className={common} data-testid={testid}>{label}</Link>;
}

export default function ModuleRenderer({ modules = [] }) {
  return (
    <>
      {modules.map((m) => {
        const d = m.data || {};
        switch (m.type) {
          case "richtext":
            return <Section key={m.id}><RichText html={d.html} className="max-w-3xl" /></Section>;

          case "image_text":
            return (
              <Section key={m.id}>
                <div className={`grid md:grid-cols-2 gap-10 items-center ${d.layout === "right" ? "md:[&>*:first-child]:order-2" : ""}`}>
                  {d.image && <img src={d.image} alt={d.alt || ""} loading="lazy" className="rounded-2xl w-full object-cover max-h-[460px] shadow-md" />}
                  <div>
                    {d.heading && <h2 className="text-2xl sm:text-3xl mb-4 text-emerald-900">{d.heading}</h2>}
                    <RichText html={d.html} />
                  </div>
                </div>
              </Section>
            );

          case "service_cards":
            return (
              <Section key={m.id}>
                <div className="grid gap-6 md:grid-cols-3">
                  {(d.services || []).map((s, i) => (
                    <div key={i} data-testid={`service-card-${s.title?.toLowerCase().replace(/\s+/g, "-")}`}
                      className="bg-white rounded-2xl border border-stone-200/80 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all overflow-hidden">
                      {s.image && <img src={s.image} alt={s.title} loading="lazy" className="h-44 w-full object-cover" />}
                      <div className="p-6">
                        <h3 className="text-xl mb-2 text-emerald-900">{s.title}</h3>
                        <p className="text-sm text-stone-600 leading-relaxed">{s.summary || s.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            );

          case "columns": {
            const cols = Math.min(4, Math.max(1, d.columns || 2));
            const grid = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" }[cols];
            return (
              <Section key={m.id}>
                <div className={`grid gap-6 ${grid}`}>
                  {(d.items || []).map((it, i) => <RichText key={i} html={it.html} className="bg-white rounded-2xl p-6 border border-stone-200/80" />)}
                </div>
              </Section>
            );
          }

          case "gallery":
            return (
              <Section key={m.id}>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {(d.images || []).map((img, i) => (
                    <img key={i} src={img.url} alt={img.alt || ""} loading="lazy" className="rounded-xl object-cover aspect-square w-full shadow-sm" />
                  ))}
                </div>
              </Section>
            );

          case "video":
            return (
              <Section key={m.id}>
                <div className="max-w-4xl mx-auto">
                  {d.heading && <h2 className="text-2xl sm:text-3xl mb-6 text-center text-emerald-900">{d.heading}</h2>}
                  <VideoEmbed url={d.url} title={d.heading} />
                </div>
              </Section>
            );

          case "testimonials":
            return (
              <Section key={m.id}>
                <div className="grid gap-6 md:grid-cols-2">
                  {(d.items || []).map((t, i) => (
                    <blockquote key={i} className="bg-white rounded-2xl p-7 border border-stone-200/80 shadow-sm">
                      <Quote className="text-emerald-300 mb-3" size={28} />
                      <p className="text-stone-700 italic leading-relaxed mb-4">{t.quote}</p>
                      <footer className="font-semibold text-emerald-800">— {t.author}</footer>
                    </blockquote>
                  ))}
                </div>
              </Section>
            );

          case "cta_banner":
            return (
              <Section key={m.id}>
                <div className="bg-gradient-to-r from-emerald-900 to-emerald-950 text-amber-50 rounded-3xl p-8 md:p-14 text-center shadow-xl border border-emerald-700/30">
                  <h2 className="text-3xl md:text-4xl text-amber-50 mb-3">{d.heading}</h2>
                  {d.text && <p className="text-stone-200 max-w-2xl mx-auto mb-7">{d.text}</p>}
                  <div className="flex flex-wrap gap-3 justify-center">
                    <CtaLink url={d.cta_url} label={d.cta_label || "Learn more"} testid="module-cta-btn" />
                  </div>
                </div>
              </Section>
            );

          case "contact_info":
            return (
              <Section key={m.id}>
                <div className="grid sm:grid-cols-3 gap-6">
                  <div className="bg-white rounded-2xl p-6 border border-stone-200/80 flex items-center gap-3"><Phone className="text-[#C85A32]" /><span className="font-mono text-sm">{d.phone}</span></div>
                  <div className="bg-white rounded-2xl p-6 border border-stone-200/80 flex items-center gap-3"><Mail className="text-emerald-700" /><span className="font-mono text-sm">{d.email}</span></div>
                  <div className="bg-white rounded-2xl p-6 border border-stone-200/80 flex items-center gap-3"><MapPin className="text-emerald-700" /><span className="text-sm">{d.address}</span></div>
                </div>
              </Section>
            );

          case "divider":
            return <div key={m.id} className="max-w-7xl mx-auto px-6"><hr className="border-stone-200" /></div>;

          default:
            return null;
        }
      })}
    </>
  );
}
